import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { api, inr, tierLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
  }, [orderId]);

  if (notFound) return <div className="py-32 text-center text-[#8C7B6E]">Order not found. <Link to="/shop" className="text-[#D4AF37]">Continue shopping</Link></div>;
  if (!order) return <div className="py-32 text-center text-[#8C7B6E]"><Loader2 className="animate-spin inline" /> Loading…</div>;

  const printInvoice = () => window.print();

  return (
    <div data-testid="order-confirmation" className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 180 }} className="text-center">
        <CheckCircle2 size={72} className="mx-auto text-[#2E7D32]" />
        <h1 className="font-display text-4xl sm:text-5xl mt-6">Order Confirmed</h1>
        <p className="text-[#C8B8AB] mt-3">Thank you! A confirmation has been sent to your email and phone.</p>
        <p className="text-[#D4AF37] font-mono-lux mt-2" data-testid="order-id">{order.order_id}</p>
      </motion.div>

      <div className="lux-card p-6 mt-10" id="invoice">
        <div className="flex justify-between items-start border-b border-[#3B251D] pb-4 mb-4">
          <div className="flex items-center gap-3">
            <img src="/brand/emblem.png" alt="" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#D4AF37]/40" />
            <div>
              <span className="font-display text-2xl gold-gradient-text">SCHOKODALE</span>
              <p className="text-[#8C7B6E] text-xs">Tax Invoice</p>
            </div>
          </div>
          <div className="text-right text-xs text-[#8C7B6E]">
            <p>Order {order.order_id}</p>
            <p className="capitalize">Status: {order.status}</p>
            <p className="capitalize">Payment: {order.payment_status}{order.payment_mock ? " (demo)" : ""}</p>
          </div>
        </div>

        <div className="text-sm text-[#C8B8AB] mb-4">
          <p className="font-semibold text-[#FDFBF7]">{order.address.name}</p>
          <p>{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}</p>
          <p>{order.address.city}, {order.address.state} — {order.address.pincode}</p>
          <p>{order.address.phone}</p>
        </div>

        <div className="space-y-2">
          {order.items.map((it) => (
            <div key={`${it.product_id}-${it.pieces}`} className="flex justify-between text-sm">
              <span className="text-[#C8B8AB]">{it.name} · {tierLabel(it.pieces)} × {it.qty}</span>
              <span className="text-[#FDFBF7]">{inr(it.line_total)}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-[#3B251D] mt-4 pt-4 space-y-2 text-sm">
          <div className="flex justify-between text-[#C8B8AB]"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-[#2E7D32]"><span>Discount ({order.coupon_code})</span><span>−{inr(order.discount)}</span></div>}
          <div className="flex justify-between text-[#C8B8AB]"><span>Delivery</span><span>{order.delivery_fee === 0 ? "Free" : inr(order.delivery_fee)}</span></div>
          {order.gift_wrap && <div className="flex justify-between text-[#C8B8AB]"><span>Gift wrap & card</span><span>{inr(order.gift_wrap_fee || 0)}</span></div>}
          <div className="flex justify-between font-display text-xl text-[#D4AF37] pt-2"><span>Total Paid</span><span>{inr(order.total)}</span></div>
        </div>
      </div>

      {order.gift_wrap && (
        <div className="lux-card p-6 mt-6" data-testid="gift-note">
          <h4 className="eyebrow mb-2">🎁 Gift Details</h4>
          {order.gift_recipient && <p className="text-[#FDFBF7] text-sm">To: {order.gift_recipient}</p>}
          {order.gift_message && <p className="text-[#C8B8AB] text-sm mt-2 italic">“{order.gift_message}”</p>}
          <p className="text-[#8C7B6E] text-xs mt-3">Your order will arrive in signature gold gift-wrap with a message card.</p>
        </div>
      )}

      <div className="flex gap-3 mt-8 justify-center print:hidden">
        <Button onClick={printInvoice} data-testid="print-invoice-btn" className="btn-outline-gold rounded-full px-6"><Download size={16} className="mr-2" /> Download Invoice</Button>
        <Link to="/account?tab=orders"><Button className="btn-gold rounded-full px-6">View My Orders</Button></Link>
      </div>
    </div>
  );
}
